import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { createAuthHeaders } from '../../hooks/useSessionToken';
import { TierBadge } from '../../components/TierBadge';
import { StatusBadge } from '../../components/StatusBadge';
import { MobileMenu, PARTNER_LINKS } from '../../components/MobileMenu';
import { API_ENDPOINTS } from '../../config/api';

interface DashboardData {
  name: string;
  tier: 'STANDARD' | 'SILVER' | 'GOLD';
  validatedThisMonth: number;
  referralsToNextTier: number | null;
  earningsThisMonth: number;
  earningsYTD: number;
  referralUrl: string;
}

interface Referral {
  id: string;
  clientName: string;
  status: string;
  createdAt: string;
  commissionRon: number | null;
}

export function Dashboard() {
  const { logout } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [recentReferrals, setRecentReferrals] = useState<Referral[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  async function fetchDashboardData() {
    try {
      // Get profile data
      const profileRes = await fetch(API_ENDPOINTS.me, { headers: createAuthHeaders() });
      if (profileRes.ok) {
        const profileData = await profileRes.json();
        setData(profileData);
      }

      // Get recent referrals
      const referralsRes = await fetch(`${API_ENDPOINTS.referrals}?page=1&limit=5`, {
        headers: createAuthHeaders(),
      });
      if (referralsRes.ok) {
        const referralsData = await referralsRes.json();
        setRecentReferrals(referralsData.data);
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal"></div>
      </div>
    );
  }

  if (!data) return null;

  const progressToNextTier = data.referralsToNextTier 
    ? Math.max(0, 9 - data.referralsToNextTier) / 9 * 100 // Assuming GOLD tier
    : 100;

  return (
    <div className="min-h-screen bg-cream">
      {/* Navigation */}
      <nav className="bg-white border-b border-border sticky top-0">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 md:space-x-8">
              <MobileMenu links={PARTNER_LINKS} onLogout={logout} />
              <span className="font-serif text-xl text-teal">BrainMap Partner</span>
              <div className="hidden md:flex space-x-4">
                <Link to="/dashboard" className="text-teal font-medium">Dashboard</Link>
                <Link to="/analytics" className="text-muted hover:text-teal">Analitică</Link>
                <Link to="/referrals" className="text-muted hover:text-teal">Referral-uri</Link>
                <Link to="/invoices" className="text-muted hover:text-teal">Facturi</Link>
                <Link to="/my-link" className="text-muted hover:text-teal">Link-ul Meu</Link>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <TierBadge tier={data.tier} />
              <button
                onClick={logout}
                className="text-sm text-muted hover:text-ink"
              >
                Deconectare
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome */}
        <div className="mb-8">
          <h1 className="text-3xl font-serif text-ink">
            Bună ziua, {data.name}!
          </h1>
          <p className="text-muted mt-1">
            Iată rezultatele tale pentru această lună
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-muted uppercase tracking-wide">Referral-uri Trimise</p>
            <p className="text-3xl font-serif text-teal mt-2">-</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-muted uppercase tracking-wide">Validate Luna Asta</p>
            <p className="text-3xl font-serif text-teal mt-2">{data.validatedThisMonth}</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-muted uppercase tracking-wide">Câștig Luna Asta</p>
            <p className="text-3xl font-serif text-teal mt-2">{data.earningsThisMonth.toLocaleString('ro-RO')} RON</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-muted uppercase tracking-wide">Câștig Total 2024</p>
            <p className="text-3xl font-serif text-teal mt-2">{data.earningsYTD.toLocaleString('ro-RO')} RON</p>
          </div>
        </div>

        {/* Tier Progress */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium text-ink">Progres spre următorul nivel</h2>
            <TierBadge tier={data.tier} />
          </div>

          {data.referralsToNextTier ? (
            <>
              <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
                <div
                  className="bg-gold h-4 rounded-full transition-all duration-500"
                  style={{ width: `${progressToNextTier}%` }}
                />
              </div>
              <p className="text-sm text-muted">
                Mai aveți nevoie de {''}
                <span className="font-medium text-teal">
                  {data.referralsToNextTier} referral-uri validate
                </span>
                {''} pentru următorul nivel
              </p>
            </>
          ) : (
            <p className="text-gold font-medium">🎉 Felicitări! Sunteți la nivelul GOLD!</p>
          )}
        </div>

        {/* Recent Referrals */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-border flex justify-between items-center">
            <h2 className="text-lg font-medium text-ink">Referral-uri Recent</h2>
            <Link to="/referrals" className="text-teal hover:underline text-sm">
              Vezi toate →
            </Link>
          </div>

          {recentReferrals.length === 0 ? (
            <div className="p-8 text-center text-muted">
              <p>Nu aveți încă niciun referral.</p>
              <p className="text-sm mt-2">Împărțiți link-ul pentru a primi primul.</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-warm">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase">Client</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase">Data</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted uppercase">Comision</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {recentReferrals.map((ref) => (
                  <tr key={ref.id} className="hover:bg-warm/50">
                    <td className="px-6 py-4 font-medium">{ref.clientName}</td>
                    <td className="px-6 py-4">
                      <StatusBadge status={ref.status} />
                    </td>
                    <td className="px-6 py-4 text-sm text-muted">
                      {new Date(ref.createdAt).toLocaleDateString('ro-RO')}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {ref.commissionRon ? (
                        <span className="font-medium text-teal">
                          {ref.commissionRon.toLocaleString('ro-RO')} RON
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
